import { useContext, useState } from 'react'

import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Instrument } from '@cash-web/protos/squareup/franklin/api/instrument.pb'
import { PasscodeCreationBlocker } from '@cash-web/protos/squareup/franklin/blockers/passcode_blockers.pb'
import { Profile } from '@cash-web/protos/squareup/franklin/ProfileProto.pb'
import { PasscodeCreationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { SetPasscodeResponseStatus } from '@cash-web/protos/squareup/franklin/SetPasscodeResponseProto.pb'
import { useAuth } from '@cash-web/shared-auth-data-access-context'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationSetPasscode } from '@cash-web/shared-data-access-api/react-query/useMutationSetPasscode'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BlockerLoading } from '../BlockerLoading/BlockerLoading'
import { getPasscodeLength } from '../BlockerPasscodeVerification/getPasscodeLength'
import { BlockerPasscodeStep } from './BlockerPasscodeStep'

export type BlockerPasscodeCreationProps = {
  blocker?: PasscodeCreationBlocker
  supplement?: PasscodeCreationBlockerSupplement
}
export type BlockerPasscodeCreationFormFields = {
  old_passcode?: string
  confirmation_passcode?: string
  new_passcode?: string
}

const defaultSteps = ['currentPasscode', 'newPasscode', 'confirmPasscode'] as const

const getVerficationInstrument = (profile: Profile): Instrument => {
  const token = profile?.verification_instrument_token
  return { ...profile?.instruments?.find(instrument => instrument.token), token }
}

export const BlockerPasscodeCreation = ({ blocker, supplement }: BlockerPasscodeCreationProps) => {
  const form = useForm({ mode: 'onTouched' })
  const { profile = {}, isLoading: isProfileLoading } = useAuth()
  const { t } = useTranslation()
  const { setError, clearError } = useContext(ErrorContext)
  const flow = useFlowContext()
  const isExistingPasscode = blocker?.requires_existing_passcode ?? false
  const [steps, setSteps] = useState(isExistingPasscode ? defaultSteps : defaultSteps.slice(1))

  const step = steps[0]

  const { mutateAsync: setPasscode } = useMutationSetPasscode({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const verificationInstrument =
    step !== 'currentPasscode' ? { token: 'CUSTOMER_PASSCODE' } : getVerficationInstrument(profile)
  const isCustomerPasscode =
    verificationInstrument.token === 'CUSTOMER_PASSCODE' || verificationInstrument.token === 'CASH_BALANCE'
  const canResetPasscode = isCustomerPasscode && step === 'currentPasscode'

  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()

  const formValues = form.getValues()
  const handleEnterPinSubmit = () => {
    clearError()
    form
      .handleSubmit(() => setSteps(steps.slice(1)))()
      .then(() => {
        // Reset the form when transitioning between steps since it should act as a new form
        form.reset({ ...formValues, confirmation_passcode: '' }, { keepIsSubmitted: false })
      })
  }

  const newPasscode = useWatch({ control: form.control, name: 'new_passcode' })
  const confirmationPasscode = useWatch({ control: form.control, name: 'confirmation_passcode' })
  const passcodeMismatch = newPasscode !== confirmationPasscode
  const handleConfirmPinSubmit = () => {
    clearError()
    form
      .handleSubmit(handleConfirmSubmitAction)()
      .then(() => {
        if (passcodeMismatch) {
          // Reset the form when transitioning between steps but maintain old passcode information
          form.reset({ old_passcode: formValues['old_passcode'] }, { keepIsSubmitted: false })
          setError(t('Your PINs don’t match. Try again.', { context: 'error message' }))
          setSteps(defaultSteps.slice(1))
        }
      })
  }

  const handleConfirmSubmitAction = async (data: BlockerPasscodeCreationFormFields) => {
    if (passcodeMismatch) {
      return
    }
    await handleBlockerResponse({
      promisedBlockerUpdate: setPasscode({
        old_passcode: data.old_passcode,
        old_instrument_token: verificationInstrument.token,
        new_passcode: data.new_passcode,
        transfer_token: flow.active?.transferToken,
        payment_tokens: flow.active?.paymentTokens,
      }),
      errors: {
        CONCURRENT_MODIFICATION: genericError,
        INVALID: t('Invalid Cash PIN. Please try again.', {
          context: 'Error to the user when the response status is INVALID',
        }),
        NONRETRYABLE_FAILURE: genericError,
        RETRYABLE_FAILURE: genericError,
      },
      onFailure(response) {
        if (!response) return
        if (response.status === SetPasscodeResponseStatus.RETRYABLE_FAILURE) {
          setSteps(defaultSteps)
        }
      },
    })
  }

  if (isProfileLoading) {
    return <BlockerLoading />
  }

  switch (step) {
    case 'currentPasscode':
      return (
        <BlockerPasscodeStep
          name="old_passcode"
          mainText={t('Enter your current PIN', { context: 'title' })}
          secondaryText={t("To change your PIN, you'll need to first enter your current PIN.", {
            context: 'subtitle',
          })}
          skippable={supplement?.skippable}
          onSubmit={handleEnterPinSubmit}
          form={form}
          canResetPasscode={canResetPasscode}
          length={getPasscodeLength(verificationInstrument)}
        />
      )
    case 'newPasscode':
      return (
        <BlockerPasscodeStep
          name="new_passcode"
          mainText={t('Create a new PIN', { context: 'title' })}
          secondaryText={t('This will be your new PIN on the app and at ATMs.', { context: 'subtitle' })}
          skippable={supplement?.skippable}
          onSubmit={handleEnterPinSubmit}
          form={form}
          canResetPasscode={canResetPasscode}
          length={getPasscodeLength(verificationInstrument)}
        />
      )
    case 'confirmPasscode':
      return (
        <BlockerPasscodeStep
          name="confirmation_passcode"
          mainText={t('Confirm your new PIN', { context: 'title' })}
          secondaryText={t('Type your new PIN again to confirm.', { context: 'subtitle' })}
          skippable={supplement?.skippable}
          onSubmit={handleConfirmPinSubmit}
          form={form}
          canResetPasscode={canResetPasscode}
          length={getPasscodeLength(verificationInstrument)}
        />
      )
  }
}
